/**
 * FronteraDeError.tsx — que una pantalla rota no se vea como una pantalla vacía.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ EL PEOR MODO DE FALLAR QUE TENÍA ESTE PRODUCTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Cuando un render lanza, React **desmonta el árbol entero**. Sin una frontera,
 * el resultado no es un mensaje ni un código: es una **página en blanco**, con
 * el menú incluido. Nada que leer, nada que reintentar, nada que contarle a
 * quien lo pueda arreglar.
 *
 * Y no es teórico. El 11 de setiembre, construyendo `/banco`:
 *
 *     TypeError: i.map is not a function
 *
 * Un `text[]` de Postgres que llegó como cadena —`postgres.js` con
 * `fetch_types: false` no deshace arreglos—, una comprobación que decía
 * `v.length > 0` (que una cadena también cumple), y la pantalla se apagó. Lo que
 * costó encontrarlo no fue el defecto: fue **que no había nada que mirar**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ LA FRONTERA VA ALREDEDOR DEL `Outlet`, NO DE LA APLICACIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A propósito, y es la decisión de forma que importa: **el menú sobrevive.**
 * Envolver la aplicación entera dejaría a la persona igual de atrapada —una
 * página de error a pantalla completa y un botón de recargar que la devuelve a
 * la misma pantalla rota—. Alrededor del contenido, lo que falla es UNA pantalla
 * y todas las demás siguen ahí, a un clic.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y SE REPONE SOLA AL CAMBIAR DE PANTALLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Una frontera que se queda pegada convierte un fallo de una pantalla en una
 * sesión perdida: se navega a otra ruta y sigue enseñando el error de la
 * anterior. Por eso lleva una `key` con la ruta —ver `Cascaron.tsx`—: React
 * monta una frontera nueva en cada navegación, y la rota se queda atrás.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ LO QUE SE ENSEÑA, Y LO QUE NO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El mensaje del error SÍ se enseña, plegado. Es la misma decisión que en la
 * API al revés y por el motivo contrario: allá el texto no viaja porque puede
 * llevar nombres de tabla de OTRO inquilino; acá ya está en el navegador de
 * quien lo sufre, y esconderlo sólo deja a la persona sin nada que copiar y
 * pegar cuando escriba a soporte.
 *
 * Lo que NO se enseña es la pila: es ruido para quien lee y no cambia nada de lo
 * que puede hacer. Va a la consola, donde vive.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';

interface Estado {
  readonly fallo: Error | null;
}

/**
 * La frontera de verdad, que tiene que ser una CLASE.
 *
 * No es una preferencia de estilo: `getDerivedStateFromError` y
 * `componentDidCatch` no existen como hooks. React no ofrece la capacidad desde
 * un componente de función, así que acá vive la única clase del proyecto.
 */
class Frontera extends Component<{ children: ReactNode; pintar: (e: Error) => ReactNode }, Estado> {
  override state: Estado = { fallo: null };

  static getDerivedStateFromError(error: Error): Estado {
    return { fallo: error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // A la consola, con la pila y el árbol de componentes. Es lo que hace que
    // el defecto se pueda localizar; lo que se dibuja es para la persona.
    console.error('Una pantalla falló', error, info.componentStack);
  }

  override render() {
    const { fallo } = this.state;
    if (fallo) return this.props.pintar(fallo);
    return this.props.children;
  }
}

/** Lo que se dibuja cuando una pantalla se cae. Aparte, porque usa el diccionario. */
function Caida({ fallo }: { readonly fallo: Error }) {
  const { t } = useIdioma();

  return (
    <div>
      <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
        <span>{t('err.screen')}</span>
      </div>
      <p className="small muted">{t('err.screenHelp')}</p>
      <details style={{ marginTop: 'var(--s6)' }}>
        <summary className="small">{t('err.detail')}</summary>
        <pre className="small" style={{ whiteSpace: 'pre-wrap', marginTop: 'var(--s4)' }}>
          {fallo.message}
        </pre>
      </details>
    </div>
  );
}

export function FronteraDeError({ children }: { readonly children: ReactNode }) {
  return <Frontera pintar={(e) => <Caida fallo={e} />}>{children}</Frontera>;
}
